import { registrationClient } from '../config/api';
import {
  ApiResponse,
  InspectionType,
  CreateInspectionTypeRequest,
  UpdateInspectionTypeRequest,
  SearchInspectionTypesParams,
} from './types/user.types';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export class InspectionTypeService {
  private readonly basePath = '/api/inspection-types';

  getAllInspectionTypes = async (): Promise<InspectionType[]> => {
    try {
      const response = await registrationClient.get<ApiResponse<InspectionType[]> | InspectionType[]>(this.basePath);

      // Suporta tanto formato com wrapper quanto direto
      if (Array.isArray(response.data)) {
        return response.data;
      }

      if (response.data?.success === false) {
        throw new Error(response.data?.errorMessage || 'Error fetching inspection types');
      }

      return response.data?.data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching inspection types');
    }
  }

  getInspectionTypeById = async (id: string): Promise<InspectionType> => {
    try {
      const response = await registrationClient.get<ApiResponse<InspectionType>>(`${this.basePath}/${id}`);

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error fetching inspection type');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching inspection type');
    }
  }

  createInspectionType = async (data: CreateInspectionTypeRequest): Promise<InspectionType | string> => {
    try {
      const response = await registrationClient.post<ApiResponse<InspectionType | string> | InspectionType | string>(this.basePath, data);

      // Suporta resposta direta ou com wrapper
      if (typeof response.data === 'string' || (response.data && 'id' in response.data && 'name' in response.data)) {
        return response.data as InspectionType | string;
      }

      if (response.data?.success === false) {
        throw new Error(response.data?.errorMessage || 'Error creating inspection type');
      }

      return (response.data?.data || response.data) as InspectionType | string;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error creating inspection type');
      throw error;
    }
  }

  updateInspectionType = async (id: string, data: UpdateInspectionTypeRequest): Promise<void> => {
    try {
      const response = await registrationClient.put<ApiResponse<void> | void>(
        `${this.basePath}/${id}`,
        data
      );

      // Só lança erro se explicitamente success = false
      if (response.data && typeof response.data === 'object' && 'success' in response.data && response.data.success === false) {
        throw new Error((response.data as ApiResponse<void>)?.errorMessage || 'Error updating inspection type');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error updating inspection type');
    }
  }

  deleteInspectionType = async (id: string): Promise<void> => {
    try {
      const response = await registrationClient.delete<ApiResponse<void>>(`${this.basePath}/${id}`);

      if (response.data && !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error deleting inspection type');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error deleting inspection type');
    }
  }

  searchInspectionTypes = async (params: SearchInspectionTypesParams): Promise<InspectionType[]> => {
    try {
      const response = await registrationClient.get<ApiResponse<InspectionType[]>>(
        `${this.basePath}/search`,
        {
          params,
        }
      );

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error searching inspection types');
      }

      return response.data.data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error searching inspection types');
    }
  }
}

export const inspectionTypeService = new InspectionTypeService();
